import Image from 'next/image';
import { Icon } from '../icons';
import { Button } from './Button';
import { cn } from '../../lib/utils';

export type PreviewChoiceCardProps = {
  title: string;
  /** Shown below the title on narrow viewports. */
  shortDescription?: string;
  /** Shown on `lg` and up instead of `shortDescription`. */
  longDescription?: string;
  /** When empty or whitespace, shows a placeholder in the media slot. */
  imageSrc?: string;
  imageAlt?: string;
  onSelect: () => void;
  /** Wide-layout hint under copy (e.g. “Get started”). The whole control remains one `button`. */
  desktopCtaLabel?: string;
  className?: string;
};

export function PreviewChoiceCard({
  title,
  shortDescription,
  longDescription,
  imageSrc,
  imageAlt,
  onSelect,
  desktopCtaLabel = 'Get started',
  className,
}: PreviewChoiceCardProps) {
  const hasImage = Boolean(imageSrc?.trim());

  return (
    <Button
      type='button'
      variant='secondary'
      onClick={onSelect}
      className={cn(
        'group flex h-auto min-h-0 w-full min-w-0 max-w-full shrink flex-row items-center gap-4 whitespace-normal rounded-xl p-4 text-left text-card-foreground shadow-sm transition-colors',
        'hover:border-primary-600/30 hover:bg-accent/40',
        'lg:flex lg:flex-col lg:items-stretch lg:gap-0 lg:p-0 lg:pb-6',
        className,
      )}
      aria-label={`${title}. ${shortDescription}`}
    >
      <div className='relative size-16 shrink-0 overflow-hidden rounded-lg border border-border bg-muted lg:aspect-[4/3] lg:size-auto lg:w-full lg:rounded-b-none lg:rounded-t-xl'>
        {hasImage ? (
          <Image
            src={imageSrc!.trim()}
            alt={imageAlt ?? ''}
            width={600}
            height={600}
            className='size-full object-cover'
            sizes='(max-width: 1024px) 64px, min(320px, 50vw)'
          />
        ) : (
          <div className='size-full bg-muted' aria-hidden />
        )}
      </div>
      <div className='min-w-0 flex-1 lg:px-6 lg:pt-5'>
        <p className='font-heading text-base font-semibold text-foreground lg:text-lg'>
          {title}
        </p>
        <p className='mt-1 text-sm leading-snug text-muted-foreground lg:hidden'>
          {shortDescription}
        </p>
        {longDescription && (
          <p className='mt-2 hidden text-sm leading-relaxed text-muted-foreground lg:block'>
            {longDescription}
          </p>
        )}
      </div>
      <Icon
        name='chevron-right'
        className='size-5 shrink-0 text-primary-600 lg:hidden'
      />
      {desktopCtaLabel ? (
        <span className='mx-6 mt-5 hidden w-auto rounded-lg bg-primary-600 px-4 py-3 text-center text-sm font-semibold text-primary-foreground lg:block'>
          {desktopCtaLabel}
        </span>
      ) : null}
    </Button>
  );
}
